import type {
  ActivityGoal,
  LoggedActivity,
  PlanPeriod,
} from '../types/activity'

export const periodMultiplier: Record<PlanPeriod, number> = {
  week: 1,
  month: 4,
  quarter: 13,
}

export const periodDays: Record<PlanPeriod, number> = {
  week: 7,
  month: 30,
  quarter: 91,
}

export const periodLabel: Record<PlanPeriod, string> = {
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
}

export type Pace = 'ahead' | 'on-track' | 'behind'

export function dailyTarget(goal: ActivityGoal, workdays: number): number {
  const days = workdays <= 0 ? 5 : workdays
  return Math.max(1, Math.round(goal.weeklyTarget / days))
}

export function targetForPeriod(goal: ActivityGoal, period: PlanPeriod): number {
  return goal.weeklyTarget * periodMultiplier[period]
}

export function actualForPeriod(
  logs: LoggedActivity[],
  category: string,
  period: PlanPeriod,
): number {
  return logs
    .filter(
      (log) => log.category === category && log.daysAgo < periodDays[period],
    )
    .reduce((total, log) => total + log.count, 0)
}

export function actualToday(
  logs: LoggedActivity[],
  category: string,
): number {
  return logs
    .filter((log) => log.category === category && log.daysAgo === 0)
    .reduce((total, log) => total + log.count, 0)
}

export function percentOf(actual: number, target: number): number {
  if (target <= 0) return 100
  return Math.min(100, Math.round((actual / target) * 100))
}

export function paceOf(actual: number, target: number): Pace {
  const percent = target <= 0 ? 100 : (actual / target) * 100
  if (percent >= 100) return 'ahead'
  if (percent >= 70) return 'on-track'
  return 'behind'
}

export const paceCopy: Record<Pace, { label: string; classes: string }> = {
  ahead: {
    label: 'Goal met',
    classes: 'bg-success-50 text-success-700 ring-success-200',
  },
  'on-track': {
    label: 'On pace',
    classes: 'bg-brand-50 text-brand-700 ring-brand-200',
  },
  behind: {
    label: 'Behind pace',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200',
  },
}
